import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticateVleUserSilentlyConfig,
  PersonaConfig,
} from '@talisdev/persona-padlock';
import { NEVER, Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthenticationFailedException } from '®shared/auth/authentication-failed-exception';
import { AppConfig } from '®shared/config/app-config';
import { ErrorReportingService } from '®shared/reporting/error-reporting.service';

import { AuthProvider } from './auth-provider';
import { AuthServiceLogin } from './auth-service-login';
import { AuthenticationFailedDoNotRetryException } from './authentication-failed-do-not-retry-exception';
import { PersonaPadlockService } from './persona-padlock.service';
import { PersonaPkceSharedService } from './persona-pkce-shared.service';
import { PersonaUser, createPersonaUserFromPkceLogin } from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

@Injectable()
export class PersonaPkceLtiAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  constructor(
    private errorReportingService: ErrorReportingService,
    private personaPadlockService: PersonaPadlockService,
    private personaPkceSharedService: PersonaPkceSharedService,
    router: Router,
    appConfig: AppConfig,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, router, userScopesService);

    this.isInLti1p3AliasWorkflow = appConfig.ltiLaunchId !== null;
  }

  private targetUri: URI | null = null;
  private isInLti1p3AliasWorkflow = false;

  public get postBreakoutLoginRedirectUri(): string {
    return this.targetUri.toString();
  }

  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();

    const ltiAuthenticationRoute = '3/lti-authentication.html';

    this.targetUri = new URI(currentUri.resource());
    return from(
      this.router.navigateByUrl(ltiAuthenticationRoute, {
        skipLocationChange: true,
      }),
    ).pipe(
      switchMap(() => NEVER),
      catchError(error =>
        this.personaPkceSharedService.throwAuthenticationFailedException(error),
      ),
    );
  }

  public getCurrentToken(): Observable<User> {
    const currentUri = this.getCurrentUri();
    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(currentUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    if (this.isInLti1p3AliasWorkflow) {
      return this.getCurrentToken1p3AliasWorkflow(currentUri, config).pipe(
        catchError(error => {
          if (!(error instanceof AuthenticationFailedDoNotRetryException)) {
            return throwError(error);
          }
          this.isInLti1p3AliasWorkflow = false;
          return this.getCurrentTokenBreakoutWorkflow(config);
        }),
      );
    } else {
      return this.getCurrentTokenBreakoutWorkflow(config);
    }
  }

  private getCurrentTokenBreakoutWorkflow(config: PersonaConfig) {
    const bypassFirstPartyCheck = true;

    return this.personaPkceSharedService.silentlyGetCurrentToken(
      this.personaPadlockService.silentlyAuthenticate(
        config,
        bypassFirstPartyCheck,
      ),
    );
  }

  private getCurrentToken1p3AliasWorkflow(
    currentUri: URI,
    config: PersonaConfig,
  ) {
    const ltiAuthNonce = currentUri.search(true).lti_auth_nonce;

    const ltiConfig: AuthenticateVleUserSilentlyConfig = {
      personaConfig: config,
      ltiAuthNonce,
      ltiAuthorizeUrl: `${this.appConfig.apiEndpoint}/lti/authorize`,
    };

    return this.personaPkceSharedService.silentlyGetCurrentToken(
      this.personaPadlockService.authenticateVleUserSilently(ltiConfig).pipe(
        catchError(error => {
          if (isLtiAliasNonceMissingError(error)) {
            return throwError(
              new AuthenticationFailedDoNotRetryException(
                'LTI alias nonce is missing',
              ),
            );
          }
          return throwError(error);
        }),
      ),
    );
  }

  public refreshToken(): Observable<User> {
    return this.personaPkceSharedService.refreshToken(this.currentUser);
  }

  public login(): void {
    const returnUrl = this.getCurrentUri();
    returnUrl.removeQuery('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase);
    returnUrl.searchParams.delete('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public performLtiBreakoutLogin(): Observable<PersonaUser> {
    if (!this.targetUri) {
      const error = new AuthenticationFailedException(
        'Unable to authenticate: No target URI set, call obtainNewToken first',
      );

      this.errorReportingService.reportToInstana(error);
      return throwError(error);
    }

    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(this.targetUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    const breakoutFunction = this.isInLti1p3AliasWorkflow
      ? this.personaPadlockService.authenticateVleUserViaBreakout
      : this.personaPadlockService.authenticateViaBreakout;

    return breakoutFunction(config).pipe(
      map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
    );
  }
}

type LtiAuthNonceMissingError = {
  error: 'LTI alias authorize error';
  error_description: 'No VLE user was found for this nonce value';
};

function isLtiAliasNonceMissingError(
  error: unknown,
): error is LtiAuthNonceMissingError {
  if (!error || typeof error !== 'object' || !(error instanceof Error)) {
    return false;
  }
  if (error.name !== 'PKCE_LTI_REQUEST_CREDENTIALS_SILENTLY_ERROR') {
    return false;
  }
  if (!('cause' in error) || typeof error['cause'] !== 'object') {
    return false;
  }
  const cause = error['cause'];
  if (!cause || typeof cause !== 'object') {
    return false;
  }
  if (!('message' in cause) || typeof cause['message'] !== 'string') {
    return false;
  }
  const message: string = cause['message'];
  const prefix = 'Invalid response: ';
  if (!message.startsWith(prefix)) {
    return false;
  }
  try {
    const errorJson = JSON.parse(message.substring(prefix.length));
    return (
      errorJson &&
      typeof errorJson === 'object' &&
      'error' in errorJson &&
      errorJson['error'] === 'LTI alias authorize error' &&
      'error_description' in errorJson &&
      errorJson['error_description'] ===
        'No VLE user was found for this nonce value'
    );
  } catch (e) {
    return false;
  }
}
